<section class="operations-page">
    <div class="operations-toolbar">
        <label class="operations-form-field" *ngIf="canFilterByOrganization">
            <span>Organization</span>
            <select [(ngModel)]="selectedOrgId" (ngModelChange)="onOrganizationChange()">
                <option value="">Current organization</option>
                <option *ngFor="let organization of organizations" [value]="organization.id">
                    {{ organization.name }}
                </option>
            </select>
        </label>

        <label class="operations-form-field">
            <span>Camera</span>
            <select [(ngModel)]="selectedCameraId" (ngModelChange)="onCameraChange()" [disabled]="loadingCameras">
                <option value="">Select camera</option>
                <option *ngFor="let camera of cameras" [value]="camera.id">
                    {{ camera.label }}
                </option>
            </select>
        </label>
    </div>

    <div class="operations-page__card" *ngIf="loadingConfig">
        <p class="operations-page__loading">Loading camera analytics configuration…</p>
    </div>

    <div class="operations-page__card" *ngIf="!loadingConfig && !selectedCameraId">
        <p class="operations-page__empty">Select a camera to configure analytics and correlation settings.</p>
    </div>

    <ng-container *ngIf="!loadingConfig && configState">
        <section class="operations-page__card operations-page__card--muted">
            <p class="operations-page__section-lead operations-page__section-lead--compact">
                <strong>Status:</strong>
                {{ customerConfigurationSourceLabel(configState.configurationSource) }}
            </p>
            <p class="operations-page__section-lead operations-page__section-lead--compact"
               *ngIf="configState.subscriptionProvisioning?.status === 'provisioned'">
                <strong>EEN subscription:</strong> Provisioned for enabled analytics.
            </p>
            <p class="operations-page__section-lead operations-page__section-lead--compact"
               *ngIf="configState.subscriptionProvisioning?.status === 'missing'">
                <strong>EEN subscription:</strong>
                {{ configState.subscriptionProvisioning.detail || 'Required subscription is missing.' }}
            </p>
            <p class="operations-page__section-lead operations-page__section-lead--compact"
               *ngIf="configState.subscriptionProvisioning?.status === 'unknown'">
                <strong>EEN subscription:</strong>
                {{ configState.subscriptionProvisioning.detail
                || 'Provisioning outcome is unknown. Analytics is not confirmed operational — contact support or retry after recovery.' }}
            </p>
            <p class="operations-page__section-lead operations-page__section-lead--compact"
               *ngIf="configState.subscriptionProvisioning?.status === 'cleanup_required'">
                <strong>EEN subscription:</strong>
                {{ configState.subscriptionProvisioning.detail
                || 'Replacement subscription is active, but a prior remote subscription still needs cleanup. Retry save or contact support.' }}
            </p>
            <p class="operations-form-hint"
               *ngIf="configState.subscriptionProvisioning?.sibling_failures?.length">
                A sibling camera had a separate subscription provisioning issue during sync
                (this camera’s own result is above). Cameras:
                {{ siblingFailureSummary }}.
            </p>
            <p class="operations-form-hint" *ngIf="!hasCameraConfiguration">
                This camera has no enabled analytics (NONE). Enable analytics below and save to configure this camera.
            </p>
            <p class="operations-form-hint" *ngIf="hasCameraConfiguration">
                Explicit analytics configuration is saved for this camera. That is separate from whether Eagle Eye
                event subscriptions are provisioned. Clearing the configuration returns this camera to no enabled
                analytics.
            </p>
            <p class="operations-form-hint" *ngIf="analyticsReadOnlyDueToAuth">
                Eagle Eye Networks provider authentication has not been set up yet.
                Complete EEN authentication under Vision Provider Management, then return here.
                If authentication was set up previously but tokens expired, saving may still fail
                until you re-authenticate.
            </p>
            <div class="operations-form-actions" *ngIf="canEdit && canClearConfiguration">
                <button type="button"
                        class="operations-btn"
                        [disabled]="saving"
                        (click)="clearConfiguration()">
                    {{ saving ? 'Working…' : 'Clear configuration' }}
                </button>
            </div>
        </section>

        <section class="operations-page__card">
            <h2 class="operations-page__heading">Enabled analytics</h2>
            <p class="operations-page__section-lead" *ngIf="!analyticsReadOnly">
                Enable or disable analytics for this camera. Vehicle Detection providers are mutually exclusive —
                at most one may be enabled (Legacy LPR or Eagle Eye LPR). Neither selected is valid.
            </p>
            <p class="operations-page__section-lead" *ngIf="analyticsReadOnlyDueToAuth">
                Editing is disabled until Eagle Eye Networks authentication is completed.
            </p>
            <p class="operations-page__section-lead" *ngIf="analyticsReadOnly && !analyticsReadOnlyDueToAuth">
                You have read-only access. Contact a KYRA platform administrator to make changes.
            </p>

            <div class="operations-page__empty operations-page__empty--inline" *ngIf="!rows.length">
                No active analytics definitions are available.
            </div>

            <ul class="camera-analytics__capabilities" *ngIf="rows.length">
                <li *ngFor="let row of rows">
                    <label class="camera-analytics__checkbox"
                           [class.camera-analytics__checkbox--disabled]="analyticsReadOnly"
                           [class.camera-analytics__checkbox--enabled]="row.isEnabled">
                        <span class="camera-analytics__check" *ngIf="row.isEnabled" aria-hidden="true">✓</span>
                        <input type="checkbox"
                               [checked]="row.isEnabled"
                               [disabled]="analyticsReadOnly"
                               (change)="onAnalyticsToggle(row.definition.id, $any($event.target).checked)">
                        <span>
                            <strong>{{ showImplementationDetails ? row.definition.name : customerAnalyticsName(row.definition) }}</strong>
                            <small *ngIf="showImplementationDetails">
                                {{ row.definition.analytics_key }} · {{ providerLabel(row.definition.provider) }}
                            </small>
                            <small *ngIf="!showImplementationDetails && customerAnalyticsDescription(row.definition) as description">
                                {{ description }}
                            </small>
                        </span>
                    </label>
                </li>
            </ul>
        </section>

        <section class="operations-page__card" *ngIf="showCorrelationSettings">
            <h2 class="operations-page__heading">Correlation settings</h2>
            <p class="operations-page__section-lead">
                Correlation matches related analytics events into a single vehicle transaction.
            </p>
            <label class="operations-form-field">
                <span>
                    Correlation window (seconds)
                    <abbr class="operations-page__help"
                          title="Time window for matching related provider events into a single vehicle transaction.">ⓘ</abbr>
                </span>
                <input type="number"
                       min="1"
                       step="1"
                       [(ngModel)]="correlationWindowSeconds"
                       [disabled]="!canEdit"
                       (ngModelChange)="onCorrelationChange()">
            </label>
        </section>

        <div class="operations-form-actions" *ngIf="canEdit">
            <p class="operations-unsaved" *ngIf="hasUnsavedChanges && eenOauthConfigured">You have unsaved changes.</p>
            <button type="button"
                    class="operations-btn operations-btn--primary"
                    [disabled]="saving || !hasUnsavedChanges || !eenOauthConfigured"
                    (click)="saveConfiguration()">
                {{ saving ? 'Saving…' : 'Save changes' }}
            </button>
        </div>
    </ng-container>
</section>
